import { useCallback, useMemo, useState } from 'react';

import { useUpdateProjectMutation } from '@features/project-management-tools/api/projectManagementApi';
import { MIX_PANEL_EVENTS } from '@features/project-management-tools/constents';

import { useMixPanelTrakingEventMutation } from '@/api/commonApis';

export const useDragAndDropUpdate = (projects, updateProjects) => {
	const [updateProject] = useUpdateProjectMutation();
	const [mixPanelTrakingEvent] = useMixPanelTrakingEventMutation();
	const [activeDragTask, setActiveDragTask] = useState(null);

	const handleUpdateSlOnDragEnd = useCallback(
		(id, newSl) => {
			const body = {
				id,
				sl: newSl.toFixed(2)
			};
			updateProject({ body, id });
		},
		[updateProject]
	);

	const handleDragEnd = useCallback(
		(event) => {
			const { active, over } = event;
			if (!active || !over) return;
			if (active.id === over.id) return;

			const items = [...(projects?.data || [])];
			const activeId = active.id;
			const overId = over.id;

			const activeIndex = items.findIndex((item) => item.id === activeId);
			const overIndex = items.findIndex((item) => item.id === overId);

			if (activeIndex === -1 || overIndex === -1) return;

			setActiveDragTask(items[activeIndex]);
			const activeItem = items.splice(activeIndex, 1)[0];

			// Calculate new SL based on position
			let newSl;
			if (overIndex === 0) {
				// If dropped at the "top", get highest SL and add 1
				const highestSl = Math.max(...items.map((item) => item.sl));
				newSl = highestSl + 1;
			} else if (overIndex === items.length - 1) {
				// If dropped at the "bottom", get lowest SL and subtract 1
				const lowestSl = Math.min(...items.map((item) => item.sl));
				newSl = lowestSl - 1;
			} else {
				// If dropped in the "middle", create avg of prev and next item's SL
				const prevSl = items[overIndex - 1].sl;
				const nextSl = items[overIndex]?.sl || 0;
				newSl = (prevSl + nextSl) / 2;
			}

			const updatedItem = {
				...activeItem,
				sl: newSl
			};

			items.splice(overIndex, 0, updatedItem);

			handleUpdateSlOnDragEnd(activeId, newSl);
			updateProjects({
				...projects,
				data: items
			});
			mixPanelTrakingEvent({
				type: MIX_PANEL_EVENTS.PROJECT_ON_DRAG,
				body: {
					projectId: activeId
				}
			});
		},
		[projects, updateProjects, handleUpdateSlOnDragEnd]
	);

	const sortedItems = useMemo(() => projects?.data || [], [projects?.data]);

	const itemIds = useMemo(() => sortedItems.map((item) => item?.id) || [], [sortedItems]);

	return {
		handleDragEnd,
		sortedItems,
		itemIds,
		activeDragTask
	};
};
